import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import { cdnConfig, CDNConfig } from './cdnConfig';

// 缓存管理
class FFmpegCache {
  private static readonly CACHE_KEY_PREFIX = 'ffmpeg_cache_';
  private static readonly CACHE_VERSION = '1.0';
  
  static async get(url: string): Promise<string | null> {
    try {
      const cacheKey = this.getCacheKey(url);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, version, timestamp } = JSON.parse(cached);
        // 检查版本和过期时间（7天）
        if (version === this.CACHE_VERSION && Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) {
          // 验证 blob URL 是否仍然有效
          if (data.startsWith('blob:')) {
            try {
              await fetch(data, { method: 'HEAD' });
              return data;
            } catch {
              // blob URL 失效，清除缓存
              localStorage.removeItem(cacheKey);
              return null;
            }
          }
          return data;
        }
      }
    } catch (error) {
      console.warn('Cache get failed:', error);
    }
    return null;
  }
  
  static async set(url: string, blobUrl: string): Promise<void> {
    try {
      const cacheKey = this.getCacheKey(url);
      const cacheData = {
        data: blobUrl,
        version: this.CACHE_VERSION,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Cache set failed:', error);
    }
  }
  
  private static getCacheKey(url: string): string {
    return this.CACHE_KEY_PREFIX + btoa(url).replace(/[^a-zA-Z0-9]/g, '');
  }
  
  static clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Cache clear failed:', error);
    }
  }
}

export interface CDNSource {
  name: string;
  coreURL: string;
  wasmURL: string;
  speed?: number;
}

export interface LoadProgress {
  stage: 'testing' | 'loading' | 'complete';
  progress: number;
  message: string;
  cdnName?: string;
}

export class FFmpegCDNLoader {
  private onProgress?: (progress: LoadProgress) => void;

  constructor(onProgress?: (progress: LoadProgress) => void) {
    this.onProgress = onProgress;
  }

  /**
   * 测试 CDN 速度
   */
  private async testCDNSpeed(url: string, timeout = 3000): Promise<number> {
    const startTime = performance.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        method: 'HEAD',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) return Infinity;

      return performance.now() - startTime;
    } catch (error) {
      return Infinity;
    }
  }

  /**
   * 获取按速度排序的 CDN 源 - 强制优先阿里云
   */
  private async getSortedSources(): Promise<CDNSource[]> {
    this.updateProgress({
      stage: 'testing',
      progress: 0,
      message: '正在清理 CDN 源...'
    });

    const sources = cdnConfig.getAllSources();
    
    // 强制优先阿里云，跳过速度测试
    const aliyunSource = sources.find(s => s.name === 'aliyun-oss');
    const otherSources = sources.filter(s => s.name !== 'aliyun-oss');
    
    if (aliyunSource) {
      this.updateProgress({
        stage: 'testing',
        progress: 50,
        message: '优先使用阿里云 OSS'
      });
      
      // 阿里云放在最前面，其他按优先级排序
      return [
        { ...aliyunSource, speed: 0 },
        ...otherSources.map(s => ({ ...s, speed: 999 }))
      ];
    }
    
    // 如果没有阿里云，按原逻辑测试
    const results = await Promise.all(
      sources.map(async (source, index) => {
        const speed = await this.testCDNSpeed(source.coreURL, 2000);

        this.updateProgress({
          stage: 'testing',
          progress: ((index + 1) / sources.length) * 50,
          message: `测试 ${source.name}: ${speed === Infinity ? '不可用' : `${speed.toFixed(0)}ms`}`
        });

        return { ...source, speed };
      })
    );

    const available = results
      .filter(r => r.speed !== Infinity)
      .sort((a, b) => a.speed! - b.speed!);

    const unavailable = results.filter(r => r.speed === Infinity);

    return [...available, ...unavailable];
  }

  /**
   * 从单个 CDN 源加载（使用缓存优化）
   */
  private async loadFromSource(ffmpeg: FFmpeg, source: CDNSource): Promise<boolean> {
    try {
      console.log(`[FFmpeg] 尝试从 ${source.name} 加载...`);
      console.log(`[FFmpeg] Core URL: ${source.coreURL}`);
      console.log(`[FFmpeg] WASM URL: ${source.wasmURL}`);

      this.updateProgress({
        stage: 'loading',
        progress: 70,
        message: `正在从 ${source.name} 下载核心文件...`
      });

      // 尝试从缓存获取
      let coreURL = await FFmpegCache.get(source.coreURL);
      if (!coreURL) {
        // 缓存未命中，下载并缓存
        console.log(`[FFmpeg] 从 ${source.name} 下载核心文件`);
        coreURL = await toBlobURL(source.coreURL, 'text/javascript');
        await FFmpegCache.set(source.coreURL, coreURL);
      } else {
        console.log(`[FFmpeg] 使用缓存的核心文件 (${source.name})`);
      }

      this.updateProgress({
        stage: 'loading',
        progress: 80,
        message: `正在从 ${source.name} 下载 WASM 文件...`
      });

      // 尝试从缓存获取 WASM
      let wasmURL = await FFmpegCache.get(source.wasmURL);
      if (!wasmURL) {
        // 缓存未命中，下载并缓存
        console.log(`[FFmpeg] 从 ${source.name} 下载 WASM 文件`);
        wasmURL = await toBlobURL(source.wasmURL, 'application/wasm');
        await FFmpegCache.set(source.wasmURL, wasmURL);
      } else {
        console.log(`[FFmpeg] 使用缓存的 WASM 文件 (${source.name})`);
      }

      this.updateProgress({
        stage: 'loading',
        progress: 90,
        message: `正在初始化 FFmpeg...`
      });

      // 加载 FFmpeg
      await ffmpeg.load({
        coreURL,
        wasmURL
      });

      console.log(`[FFmpeg] ✅ 成功从 ${source.name} 加载`);
      return true;
    } catch (error) {
      console.warn(`[FFmpeg] ❌ ${source.name} 加载失败:`, error);
      return false;
    }
  }

  /**
   * 带超时的加载
   */
  private async loadFromSourceWithTimeout(
    ffmpeg: FFmpeg,
    source: CDNSource,
    timeout: number = 60000
  ): Promise<boolean> {
    return Promise.race([
      this.loadFromSource(ffmpeg, source),
      new Promise<boolean>((_, reject) =>
        setTimeout(() => reject(new Error(`加载超时（${timeout / 1000}秒）`)), timeout)
      )
    ]).catch(error => {
      console.warn(`[FFmpeg] ${source.name} 加载失败:`, error);
      return false;
    });
  }

  /**
   * 智能加载 FFmpeg
   */
  async loadFFmpeg(): Promise<{ ffmpeg: FFmpeg; cdnName: string }> {
    const ffmpeg = new FFmpeg();

    // ⭐ 只设置日志回调，不设置progress回调（避免干扰后续处理进度）
    ffmpeg.on('log', ({ message }) => {
      console.log('[FFmpeg]', message);
    });

    try {
      // 1. 获取排序后的 CDN 源
      const sortedSources = await this.getSortedSources();

      if (sortedSources.filter(s => s.speed !== Infinity).length === 0) {
        throw new Error('没有可用的 CDN 源');
      }

      // 2. 依次尝试每个 CDN（从最快的开始，带超时机制）
      let cdnName: string | null = null;

      for (const source of sortedSources) {
        if (source.speed === Infinity) continue; // 跳过不可用的

        this.updateProgress({
          stage: 'loading',
          progress: 60,
          message: `尝试从 ${source.name} 加载...`
        });

        // 使用带超时的加载方法（60秒超时）
        const success = await this.loadFromSourceWithTimeout(ffmpeg, source, 60000);
        if (success) {
          cdnName = source.name;
          break;
        }

        // 如果失败，继续尝试下一个CDN
        console.log(`[FFmpeg] ${source.name} 失败，尝试下一个 CDN...`);
      }

      if (!cdnName) {
        throw new Error('所有 CDN 源均加载失败');
      }

      this.updateProgress({
        stage: 'complete',
        progress: 100,
        message: `FFmpeg 加载完成 (${cdnName})`,
        cdnName
      });

      return { ffmpeg, cdnName };

    } catch (error) {
      console.error('[FFmpeg] 加载失败:', error);
      throw new Error(`FFmpeg 加载失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  /**
   * 更新进度
   */
  private updateProgress(progress: LoadProgress): void {
    if (this.onProgress) {
      this.onProgress(progress);
    }
  }

  /**
   * 清除缓存
   */
  static clearCache(): void {
    FFmpegCache.clear();
    console.log('[FFmpeg] 缓存已清除');
  }
}